import logger from "@logger";
import CompositionTree from "@shared/components/animationPlayground/CompositionTree";
import InspectorPanel from "@shared/components/animationPlayground/InspectorPanel";
import {
  buildCompositionRows,
  normalizeLottiePlaygroundJson,
  stripJitterWatermarkFromLottieJson,
} from "@shared/utils/lottiePlaygroundModel";
import clsx from "clsx";
import Lottie, { type LottieRefCurrentProps } from "lottie-react";
import { Download, Eraser, FolderPlus, Pause, Play, RotateCcw, Upload } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type Preset = { id: string; label: string; data: unknown };

const presetModules = import.meta.glob("../../assets/animations/*.json", {
  eager: true,
  import: "default",
}) as Record<string, unknown>;

function buildPresets(): Preset[] {
  const list: Preset[] = [];
  for (const filePath of Object.keys(presetModules)) {
    const label = filePath.match(/([^/\\]+)\.json$/u)?.[1]?.replace(/_/gu, " ") ?? filePath;
    list.push({
      id: filePath,
      label,
      data: presetModules[filePath],
    });
  }
  list.sort((a, b) => a.label.localeCompare(b.label));
  return list;
}

function serialise(data: unknown): string {
  return `${JSON.stringify(data, null, 2)}\n`;
}

const DEV_SAVE_ASSET_URL = "/__dev/animation-playground/save-asset";

function suggestedAssetFileBaseName(selectedPresetId: string): string {
  if (!selectedPresetId) {
    return "MyAnimation";
  }
  const m = selectedPresetId.match(/([^/\\]+)\.json$/u);
  const raw = m?.[1]?.replace(/\s+/gu, "_") ?? "MyAnimation";
  return raw.length > 0 ? raw : "MyAnimation";
}

interface LottieTimingFields {
  ip?: number;
  op?: number;
}

function frameRange(data: unknown): { min: number; max: number } {
  if (!data || typeof data !== "object") {
    return { min: 0, max: 0 };
  }
  const d = data as LottieTimingFields;
  const ip = typeof d.ip === "number" ? d.ip : 0;
  const op = typeof d.op === "number" ? d.op : 0;
  const max = op > ip ? Math.ceil(op) - 1 : Math.ceil(ip);
  return { min: Math.floor(ip), max: Math.max(Math.floor(ip), max) };
}

export default function AnimationPlayground() {
  const presets = useMemo(buildPresets, []);
  const initialNormalised = useMemo(() => normalizeLottiePlaygroundJson(presets[0]?.data ?? {}), [presets]);
  const [selectedPresetId, setSelectedPresetId] = useState<string>(() => presets[0]?.id ?? "");
  const [jsonText, setJsonText] = useState<string>(() => serialise(initialNormalised.data));
  const [parseError, setParseError] = useState<string | null>(null);
  const [animationData, setAnimationData] = useState<unknown>(() => initialNormalised.data);
  const [playerKey, setPlayerKey] = useState(0);
  const [loop, setLoop] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [reversed, setReversed] = useState(false);
  const [previewScale, setPreviewScale] = useState(100);
  const [scrubFrame, setScrubFrame] = useState(() => frameRange(initialNormalised.data).min);
  const [frameBounds, setFrameBounds] = useState(() => frameRange(initialNormalised.data));
  const lottieRef = useRef<LottieRefCurrentProps | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [inspectorPathKey, setInspectorPathKey] = useState<string | null>(null);
  const [assetSaveName, setAssetSaveName] = useState(() => suggestedAssetFileBaseName(presets[0]?.id ?? ""));
  const [assetSaveStatus, setAssetSaveStatus] = useState<string | null>(null);
  const [watermarkStripMessage, setWatermarkStripMessage] = useState<string | null>(null);

  const showDevSaveToAssets = import.meta.env.DEV && import.meta.env.MODE === "development";

  const compositionRows = useMemo(() => buildCompositionRows(animationData), [animationData]);

  const commitInspectorMutation = useCallback((nextRoot: unknown) => {
    setAnimationData(nextRoot);
    setJsonText(serialise(nextRoot));
    setParseError(null);
    setPlayerKey((k) => k + 1);
  }, []);

  const applyParsed = useCallback((parsed: unknown, sourceKey?: string) => {
    const { data, repairedTextFillCount } = normalizeLottiePlaygroundJson(parsed);
    if (repairedTextFillCount > 0) {
      logger.info(`Animation playground: inserted default text fill (fc) on ${repairedTextFillCount} text layer(s).`);
    }
    setAnimationData(data);
    setJsonText(serialise(data));
    setParseError(null);
    setPlayerKey((k) => k + 1);
    const range = frameRange(data);
    setFrameBounds(range);
    setScrubFrame(range.min);
    if (sourceKey != null) {
      setSelectedPresetId(sourceKey);
    }
  }, []);

  const handleApply = useCallback(() => {
    try {
      const parsed: unknown = JSON.parse(jsonText);
      setWatermarkStripMessage(null);
      applyParsed(parsed);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Invalid JSON";
      setParseError(message);
    }
  }, [applyParsed, jsonText]);

  const handleStripJitterWatermark = useCallback(() => {
    setWatermarkStripMessage(null);
    setParseError(null);
    try {
      const parsed: unknown = JSON.parse(jsonText);
      const stripped = stripJitterWatermarkFromLottieJson(parsed);
      if (stripped.removedLayerCount === 0 && stripped.removedAssetCount === 0 && !stripped.metaCleared) {
        setWatermarkStripMessage("No Jitter watermark found (no refId 8/16 layers, unused assets, or meta.g).");
        return;
      }
      const { data, repairedTextFillCount } = normalizeLottiePlaygroundJson(stripped.data);
      if (repairedTextFillCount > 0) {
        logger.info(`Animation playground: inserted default text fill (fc) on ${repairedTextFillCount} text layer(s).`);
      }
      applyParsed(data);
      setSelectedPresetId("");
      setInspectorPathKey(null);
      const parts = [
        `Removed ${stripped.removedLayerCount} layer(s) and ${stripped.removedAssetCount} unused asset(s).`,
        stripped.metaCleared ? "Cleared meta.g." : null,
      ].filter(Boolean);
      setWatermarkStripMessage(parts.join(" "));
      logger.info("Animation playground: stripped Jitter watermark", {
        removedLayerCount: stripped.removedLayerCount,
        removedAssetCount: stripped.removedAssetCount,
        metaCleared: stripped.metaCleared,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : "Invalid JSON";
      setParseError(message);
    }
  }, [applyParsed, jsonText]);

  const handlePresetChange = useCallback(
    (id: string) => {
      const preset = presets.find((p) => p.id === id);
      if (!preset) {
        return;
      }
      const text = serialise(preset.data);
      setJsonText(text);
      applyParsed(preset.data, id);
      setInspectorPathKey(null);
      setAssetSaveName(suggestedAssetFileBaseName(id));
      setWatermarkStripMessage(null);
    },
    [applyParsed, presets]
  );

  const handleFile = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        const text = typeof reader.result === "string" ? reader.result : "";
        setJsonText(text);
        try {
          const parsed: unknown = JSON.parse(text);
          applyParsed(parsed);
          setSelectedPresetId("");
          setInspectorPathKey(null);
          const base = file.name.replace(/\.json$/iu, "").replace(/[^\w.-]+/gu, "_");
          if (base.length > 0) {
            setAssetSaveName(base.slice(0, 120));
          }
          setWatermarkStripMessage(null);
        } catch (e) {
          const message = e instanceof Error ? e.message : "Invalid JSON";
          setParseError(message);
        }
      };
      reader.onerror = () => {
        setParseError("Could not read file");
        logger.error(new Error("Animation playground file read failed"));
      };
      reader.readAsText(file);
    },
    [applyParsed]
  );

  useEffect(() => {
    const api = lottieRef.current;
    if (!api?.setSpeed) {
      return;
    }
    api.setSpeed(speed);
  }, [speed]);

  useEffect(() => {
    const api = lottieRef.current;
    if (!api?.setDirection) {
      return;
    }
    api.setDirection(reversed ? -1 : 1);
  }, [reversed]);

  const handlePlay = useCallback(() => {
    lottieRef.current?.play();
  }, []);

  const handlePause = useCallback(() => {
    lottieRef.current?.pause();
  }, []);

  const handleStop = useCallback(() => {
    const api = lottieRef.current;
    api?.stop();
    const min = frameBounds.min;
    api?.goToAndStop(min, true);
    setScrubFrame(min);
  }, [frameBounds.min]);

  const handleDownload = useCallback(() => {
    const blob = new Blob([jsonText], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "animation.json";
    anchor.click();
    URL.revokeObjectURL(url);
  }, [jsonText]);

  const handleAddToAssets = useCallback(async () => {
    setAssetSaveStatus(null);
    try {
      JSON.parse(jsonText) as unknown;
    } catch {
      setAssetSaveStatus("Fix JSON (or click Apply) before saving to the repo.");
      return;
    }
    try {
      const res = await fetch(DEV_SAVE_ASSET_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fileBaseName: assetSaveName, jsonText }),
      });
      const data = (await res.json()) as { ok?: boolean; relativePath?: string; error?: string };
      if (data.ok === true && typeof data.relativePath === "string") {
        setAssetSaveStatus(
          `Saved ${data.relativePath}. Restart dev server or refresh if the preset list does not update.`
        );
        logger.info(`Animation playground: wrote ${data.relativePath}`);
      } else {
        setAssetSaveStatus(data.error ?? `Save failed (${res.status})`);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : "request_failed";
      setAssetSaveStatus(message);
      logger.error(e instanceof Error ? e : new Error("Animation playground save to assets failed"));
    }
  }, [assetSaveName, jsonText]);

  const onEnterFrame = useCallback(() => {
    const raw = lottieRef.current?.animationItem?.currentFrame;
    if (typeof raw === "number") {
      setScrubFrame(Math.round(raw));
    }
  }, []);

  const onDOMLoaded = useCallback(() => {
    const api = lottieRef.current;
    const jsonRange = frameRange(animationData);
    const dur = api?.getDuration?.(true);
    let max = jsonRange.max;
    if (dur != null && Number.isFinite(dur)) {
      max = Math.max(jsonRange.min, Math.round(dur) - 1);
    }
    setFrameBounds({ min: jsonRange.min, max });
    const frame = api?.animationItem?.currentFrame;
    if (typeof frame === "number") {
      setScrubFrame(Math.round(frame));
    }
    api?.setSpeed(speed);
    api?.setDirection(reversed ? -1 : 1);
  }, [animationData, reversed, speed]);

  const scrubMin = frameBounds.min;
  const scrubMax = frameBounds.max;

  const handleScrub = useCallback(
    (value: number) => {
      const clamped = Math.min(scrubMax, Math.max(scrubMin, value));
      setScrubFrame(clamped);
      lottieRef.current?.pause();
      lottieRef.current?.goToAndStop(clamped, true);
    },
    [scrubMax, scrubMin]
  );

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="animation-playground-root">
      <div className="mx-auto max-w-7xl px-4 py-8 space-y-6">
        <header className="space-y-2 border-b border-zinc-800 pb-6">
          <h1 className="text-2xl font-semibold tracking-tight">Animation playground</h1>
          <p className="text-sm text-zinc-400">
            Inspect layers and shapes, edit colours (static), read keyframes, scrub the timeline, and export JSON.
          </p>
        </header>

        <div className="grid gap-4 xl:grid-cols-[minmax(11rem,13rem)_minmax(0,1fr)_minmax(14rem,18rem)]">
          <aside className="space-y-3 rounded-xl border border-zinc-800 bg-zinc-900/50 p-3 xl:max-h-[calc(100vh-10rem)] xl:overflow-y-auto">
            <CompositionTree onSelect={setInspectorPathKey} rows={compositionRows} selectedKey={inspectorPathKey} />
          </aside>

          <section className="space-y-4 rounded-xl border border-zinc-800 bg-zinc-900/50 p-4">
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex min-w-[12rem] flex-col gap-1 text-sm">
                <span className="text-zinc-400">Preset</span>
                <select
                  className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-zinc-100"
                  data-testid="animation-playground-preset-select"
                  onChange={(e) => handlePresetChange(e.target.value)}
                  value={selectedPresetId}
                >
                  <option value="">Custom</option>
                  {presets.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1 text-sm">
                <span className="text-zinc-400">Preview scale</span>
                <input
                  className="w-40 accent-violet-500"
                  data-testid="animation-playground-scale"
                  max={200}
                  min={25}
                  onChange={(e) => setPreviewScale(Number(e.target.value))}
                  type="range"
                  value={previewScale}
                />
              </label>
              <span className="pb-2 text-sm tabular-nums text-zinc-500">{previewScale}%</span>
            </div>

            <div
              className="flex min-h-[280px] items-center justify-center overflow-hidden rounded-lg bg-zinc-950 ring-1 ring-zinc-800"
              data-testid="animation-playground-stage"
            >
              <div
                style={{
                  transform: `scale(${previewScale / 100})`,
                  transformOrigin: "center center",
                }}
              >
                <Lottie
                  key={playerKey}
                  animationData={animationData}
                  loop={loop}
                  lottieRef={lottieRef}
                  onDOMLoaded={onDOMLoaded}
                  onEnterFrame={onEnterFrame}
                  data-testid="animation-playground-lottie"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-500"
                data-testid="animation-playground-play"
                onClick={handlePlay}
                type="button"
              >
                <Play className="size-4" aria-hidden />
                Play
              </button>
              <button
                className="inline-flex items-center gap-2 rounded-lg border border-zinc-600 bg-zinc-900 px-3 py-2 text-sm hover:bg-zinc-800"
                data-testid="animation-playground-pause"
                onClick={handlePause}
                type="button"
              >
                <Pause className="size-4" aria-hidden />
                Pause
              </button>
              <button
                className="inline-flex items-center gap-2 rounded-lg border border-zinc-600 bg-zinc-900 px-3 py-2 text-sm hover:bg-zinc-800"
                data-testid="animation-playground-stop"
                onClick={handleStop}
                type="button"
              >
                <RotateCcw className="size-4" aria-hidden />
                Stop
              </button>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                checked={loop}
                className="size-4 accent-violet-500"
                data-testid="animation-playground-loop"
                onChange={(e) => setLoop(e.target.checked)}
                type="checkbox"
              />
              Loop
            </label>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-zinc-400">Speed</span>
              <input
                className="accent-violet-500"
                data-testid="animation-playground-speed"
                max={3}
                min={0.25}
                onChange={(e) => setSpeed(Number(e.target.value))}
                step={0.25}
                type="range"
                value={speed}
              />
              <span className="text-xs tabular-nums text-zinc-500">{speed.toFixed(2)}×</span>
            </label>

            <label className="flex items-center gap-2 text-sm">
              <input
                checked={reversed}
                className="size-4 accent-violet-500"
                data-testid="animation-playground-reverse"
                onChange={(e) => setReversed(e.target.checked)}
                type="checkbox"
              />
              Reverse direction
            </label>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-zinc-400">
                Frame {scrubFrame} ({scrubMin}–{scrubMax})
              </span>
              <input
                className="accent-violet-500"
                data-testid="animation-playground-scrub"
                max={scrubMax}
                min={scrubMin}
                onChange={(e) => handleScrub(Number(e.target.value))}
                type="range"
                value={scrubFrame}
              />
            </label>
          </section>

          <aside className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-3 xl:max-h-[calc(100vh-10rem)] xl:overflow-y-auto">
            <InspectorPanel
              animationData={animationData}
              key={inspectorPathKey ?? "__none__"}
              onCommitAnimation={commitInspectorMutation}
              selectedPathKey={inspectorPathKey}
            />
          </aside>
        </div>

        <section className="mt-6 flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            <button
              className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-500"
              data-testid="animation-playground-apply"
              onClick={handleApply}
              type="button"
            >
              Apply JSON
            </button>
            <button
              className="inline-flex items-center gap-2 rounded-lg border border-zinc-600 bg-zinc-900 px-3 py-2 text-sm hover:bg-zinc-800"
              data-testid="animation-playground-strip-jitter-watermark"
              onClick={handleStripJitterWatermark}
              type="button"
            >
              <Eraser className="size-4" aria-hidden />
              Strip Jitter watermark
            </button>
            <button
              className="inline-flex items-center gap-2 rounded-lg border border-zinc-600 bg-zinc-900 px-3 py-2 text-sm hover:bg-zinc-800"
              data-testid="animation-playground-upload"
              onClick={() => fileInputRef.current?.click()}
              type="button"
            >
              <Upload className="size-4" aria-hidden />
              Load file
            </button>
            <input
              accept=".json,application/json"
              className="hidden"
              data-testid="animation-playground-file-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) {
                  handleFile(file);
                }
              }}
              ref={fileInputRef}
              type="file"
            />
            <button
              className="inline-flex items-center gap-2 rounded-lg border border-zinc-600 bg-zinc-900 px-3 py-2 text-sm hover:bg-zinc-800"
              data-testid="animation-playground-download"
              onClick={handleDownload}
              type="button"
            >
              <Download className="size-4" aria-hidden />
              Download JSON
            </button>
            {showDevSaveToAssets ? (
              <>
                <label className="flex min-w-[10rem] flex-col gap-1 text-sm">
                  <span className="text-zinc-400">Repo filename</span>
                  <input
                    className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 font-mono text-xs text-zinc-100"
                    data-testid="animation-playground-asset-filename"
                    onChange={(e) => setAssetSaveName(e.target.value)}
                    spellCheck={false}
                    type="text"
                    value={assetSaveName}
                  />
                </label>
                <button
                  className="inline-flex items-center gap-2 rounded-lg border border-amber-700/80 bg-amber-950/40 px-3 py-2 text-sm text-amber-100 hover:bg-amber-900/50"
                  data-testid="animation-playground-add-to-assets"
                  onClick={() => {
                    void handleAddToAssets();
                  }}
                  type="button"
                >
                  <FolderPlus className="size-4" aria-hidden />
                  Add to assets
                </button>
              </>
            ) : null}
          </div>

          {assetSaveStatus ? (
            <p className="text-xs text-zinc-400" data-testid="animation-playground-asset-save-status">
              {assetSaveStatus}
            </p>
          ) : null}

          {watermarkStripMessage ? (
            <p className="text-xs text-zinc-400" data-testid="animation-playground-watermark-strip-status">
              {watermarkStripMessage}
            </p>
          ) : null}

          {parseError ? (
            <p
              className="rounded-lg border border-red-900/80 bg-red-950/50 px-3 py-2 text-sm text-red-200"
              data-testid="animation-playground-parse-error"
            >
              {parseError}
            </p>
          ) : null}

          <textarea
            className={clsx(
              "min-h-[480px] flex-1 w-full resize-y rounded-lg border px-3 py-2 font-mono text-xs leading-relaxed",
              parseError ? "border-red-800 bg-zinc-950 text-red-100" : "border-zinc-700 bg-zinc-950 text-zinc-100"
            )}
            data-testid="animation-playground-json"
            onChange={(e) => setJsonText(e.target.value)}
            spellCheck={false}
            value={jsonText}
          />
        </section>
      </div>
    </div>
  );
}
